import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  ShieldCheck,
  Clock,
  CreditCard,
  AlertTriangle,
  Scale,
  Ban,
  Phone,
  CalendarCheck,
  Globe
} from 'lucide-react';

const TermsPage = () => {
  const { i18n } = useTranslation();
  const isHindi = i18n.language === 'hi';

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-24 px-6 md:px-12 lg:px-24 pb-20">

      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <div className="text-center mb-16">

          <h1
            className="text-4xl md:text-5xl font-serif text-[#F5F2EB] mb-4"
            data-testid="terms-heading"
          >
            {isHindi
              ? 'नियम एवं शर्तें'
              : 'Terms & Conditions'}
          </h1>

          <p className="text-[#C9A84C] text-lg tracking-wide">
            {isHindi
              ? 'ज्योतिष एवं वास्तु परामर्श सेवाओं के उपयोग हेतु नियम'
              : 'Guidelines for Astrology & Vastu Consultation Services'}
          </p>

        </div>

        {/* Intro */}
        <div className="bg-[#111111] border border-[#C9A84C]/10 rounded-3xl p-8 mb-10">

          <div className="flex items-center gap-4 mb-5">
            <FileText className="text-[#C9A84C]" size={28} />

            <h2 className="text-2xl font-serif text-[#F5F2EB]">
              {isHindi
                ? 'सेवाओं का उपयोग'
                : 'Use of Services'}
            </h2>
          </div>

          <p className="text-[#A3A3A3] leading-relaxed text-lg">
            {isHindi
              ? 'इस वेबसाइट एवं हमारी ज्योतिष, वास्तु तथा आध्यात्मिक परामर्श सेवाओं का उपयोग करके, आप निम्नलिखित नियम एवं शर्तों से सहमत होते हैं। कृपया सेवाओं का उपयोग करने से पहले इन्हें ध्यानपूर्वक पढ़ें।'
              : 'By accessing this website and using our astrology, vastu, and spiritual consultation services, you agree to comply with the following terms and conditions. Please read them carefully before using our services.'}
          </p>

        </div>

        <div className="space-y-8">

          {/* Eligibility */}
          <div className="bg-[#111111] border border-[#C9A84C]/10 rounded-3xl p-8">

            <div className="flex items-center gap-4 mb-6">
              <ShieldCheck className="text-[#C9A84C]" size={24} />

              <h2 className="text-2xl font-serif text-[#F5F2EB]">
                {isHindi
                  ? 'पात्रता'
                  : 'Eligibility'}
              </h2>
            </div>

            <ul className="space-y-4 text-[#A3A3A3] leading-relaxed">

              <li>
                • {isHindi
                  ? 'हमारी सेवाएँ केवल 18 वर्ष या उससे अधिक आयु के व्यक्तियों के लिए उपलब्ध हैं।'
                  : 'Our services are intended only for individuals aged 18 years or above.'}
              </li>

              <li>
                • {isHindi
                  ? '18 वर्ष से कम आयु के उपयोगकर्ताओं को अभिभावक की अनुमति आवश्यक है।'
                  : 'Users below 18 years must use the services only with parental or guardian consent.'}
              </li>

            </ul>

          </div>

          {/* Accuracy */}
          <div className="bg-[#111111] border border-[#C9A84C]/10 rounded-3xl p-8">

            <div className="flex items-center gap-4 mb-6">
              <AlertTriangle className="text-[#C9A84C]" size={24} />

              <h2 className="text-2xl font-serif text-[#F5F2EB]">
                {isHindi
                  ? 'सही जानकारी प्रदान करना'
                  : 'Providing Accurate Information'}
              </h2>
            </div>

            <ul className="space-y-4 text-[#A3A3A3] leading-relaxed">

              <li>
                • {isHindi
                  ? 'ग्राहक को जन्म तिथि, समय, स्थान एवं अन्य आवश्यक जानकारी सही रूप में प्रदान करनी होगी।'
                  : 'Clients must provide accurate birth details, time, location, and other required information.'}
              </li>

              <li>
                • {isHindi
                  ? 'गलत या अधूरी जानकारी के कारण प्राप्त परिणामों की जिम्मेदारी हमारी नहीं होगी।'
                  : 'We are not responsible for inaccurate readings or guidance caused by incorrect or incomplete information.'}
              </li>

            </ul>

          </div>

          {/* Consultation */}
          <div className="bg-[#111111] border border-[#C9A84C]/10 rounded-3xl p-8">

            <div className="flex items-center gap-4 mb-6">
              <CalendarCheck className="text-[#C9A84C]" size={24} />

              <h2 className="text-2xl font-serif text-[#F5F2EB]">
                {isHindi
                  ? 'परामर्श एवं अपॉइंटमेंट नियम'
                  : 'Consultation & Appointment Policy'}
              </h2>
            </div>

            <ul className="space-y-4 text-[#A3A3A3] leading-relaxed">

              <li>
                • {isHindi
                  ? 'सभी परामर्श निर्धारित समय पर प्रारंभ होंगे।'
                  : 'All consultations will begin at the scheduled time.'}
              </li>

              <li>
                • {isHindi
                  ? 'यदि ग्राहक देर से जुड़ता है, तो सत्र का समय नहीं बढ़ाया जाएगा।'
                  : 'If the client joins late, the session duration will not be extended.'}
              </li>

              <li>
                • {isHindi
                  ? 'ऑनलाइन एवं ऑफलाइन दोनों प्रकार के परामर्श पूर्व बुकिंग पर आधारित होंगे।'
                  : 'Both online and offline consultations are available through prior booking only.'}
              </li>

              <li>
                • {isHindi
                  ? 'वास्तु साइट विजिट हेतु अतिरिक्त यात्रा, भोजन एवं रहने का खर्च ग्राहक द्वारा वहन किया जाएगा।'
                  : 'For vastu site visits, travel, accommodation, and related expenses will be borne by the client.'}
              </li>

            </ul>

          </div>

          {/* Reschedule */}
          <div className="bg-[#111111] border border-[#C9A84C]/10 rounded-3xl p-8">

            <div className="flex items-center gap-4 mb-6">
              <Clock className="text-[#C9A84C]" size={24} />

              <h2 className="text-2xl font-serif text-[#F5F2EB]">
                {isHindi
                  ? 'रद्दीकरण एवं पुनर्निर्धारण'
                  : 'Cancellation & Rescheduling'}
              </h2>
            </div>

            <ul className="space-y-4 text-[#A3A3A3] leading-relaxed">

              <li>
                • {isHindi
                  ? 'सत्र से 24 घंटे पहले तक पुनर्निर्धारण की अनुमति है।'
                  : 'Rescheduling is allowed up to 24 hours before the consultation.'}
              </li>

              <li>
                • {isHindi
                  ? '24 घंटे पहले रद्द करने पर 50% तक आंशिक रिफंड दिया जा सकता है।'
                  : 'Partial refund up to 50% may be provided for cancellations made 24 hours in advance.'}
              </li>

              <li>
                • {isHindi
                  ? 'सत्र पूर्ण होने के बाद कोई रिफंड नहीं दिया जाएगा।'
                  : 'No refund will be issued after the consultation is completed.'}
              </li>

              <li>
                • {isHindi
                  ? 'सत्र से 4 घंटे से कम समय पहले रद्द करने पर कोई रिफंड नहीं होगा।'
                  : 'No refund will be provided for cancellations made less than 4 hours before the session.'}
              </li>

            </ul>

          </div>

          {/* Payment */}
          <div className="bg-[#111111] border border-[#C9A84C]/10 rounded-3xl p-8">

            <div className="flex items-center gap-4 mb-6">
              <CreditCard className="text-[#C9A84C]" size={24} />

              <h2 className="text-2xl font-serif text-[#F5F2EB]">
                {isHindi
                  ? 'भुगतान शर्तें'
                  : 'Payment Terms'}
              </h2>
            </div>

            <p className="text-[#A3A3A3] leading-relaxed">
              {isHindi
                ? 'सभी भुगतान सुरक्षित पेमेंट गेटवे के माध्यम से किए जाएंगे। सेवा शुरू होने से पहले पूर्ण भुगतान आवश्यक हो सकता है। भुगतान विफल होने की स्थिति में बुकिंग स्वतः रद्द मानी जाएगी।'
                : 'All payments must be made through secure payment gateways. Full payment may be required before the consultation begins. Bookings may be cancelled automatically in case of failed payments.'}
            </p>

          </div>

          {/* Disclaimer */}
          <div className="bg-[#111111] border border-[#C9A84C]/10 rounded-3xl p-8">

            <div className="flex items-center gap-4 mb-6">
              <Scale className="text-[#C9A84C]" size={24} />

              <h2 className="text-2xl font-serif text-[#F5F2EB]">
                {isHindi
                  ? 'अस्वीकरण'
                  : 'Disclaimer'}
              </h2>
            </div>

            <ul className="space-y-4 text-[#A3A3A3] leading-relaxed">

              <li>
                • {isHindi
                  ? 'ज्योतिष एवं वास्तु परामर्श मार्गदर्शन एवं आध्यात्मिक सहायता के उद्देश्य से प्रदान किए जाते हैं।'
                  : 'Astrology and vastu consultations are provided for guidance and spiritual support purposes only.'}
              </li>

              <li>
                • {isHindi
                  ? 'हम किसी विशेष परिणाम, लाभ या सफलता की गारंटी नहीं देते।'
                  : 'We do not guarantee any specific outcome, success, or result.'}
              </li>

              <li>
                • {isHindi
                  ? 'परामर्श चिकित्सा, कानूनी, वित्तीय या मनोवैज्ञानिक सलाह का विकल्प नहीं है।'
                  : 'Consultations are not a substitute for medical, legal, financial, or psychological advice.'}
              </li>

              <li>
                • {isHindi
                  ? 'निर्णय लेने की अंतिम जिम्मेदारी उपयोगकर्ता की स्वयं की होगी।'
                  : 'Final decisions and actions taken based on consultations remain the sole responsibility of the client.'}
              </li>

            </ul>

          </div>

          {/* Behaviour */}
          <div className="bg-[#111111] border border-[#C9A84C]/10 rounded-3xl p-8">

            <div className="flex items-center gap-4 mb-6">
              <Ban className="text-[#C9A84C]" size={24} />

              <h2 className="text-2xl font-serif text-[#F5F2EB]">
                {isHindi
                  ? 'अनुचित व्यवहार नीति'
                  : 'Respectful Conduct Policy'}
              </h2>
            </div>

            <p className="text-[#A3A3A3] leading-relaxed">
              {isHindi
                ? 'हम किसी भी प्रकार के अपमानजनक, धमकीपूर्ण या अनुचित व्यवहार करने वाले व्यक्ति को सेवा देने से मना करने का अधिकार सुरक्षित रखते हैं।'
                : 'We reserve the right to refuse or terminate services for any individual displaying abusive, threatening, or disrespectful behavior.'}
            </p>

          </div>

          {/* Governing Law */}
          <div className="bg-[#111111] border border-[#C9A84C]/10 rounded-3xl p-8">

            <div className="flex items-center gap-4 mb-6">
              <Globe className="text-[#C9A84C]" size={24} />

              <h2 className="text-2xl font-serif text-[#F5F2EB]">
                {isHindi
                  ? 'प्रचलित कानून'
                  : 'Governing Law'}
              </h2>
            </div>

            <p className="text-[#A3A3A3] leading-relaxed">
              {isHindi
                ? 'ये नियम एवं शर्तें भारत के कानूनों के अंतर्गत संचालित होंगी। किसी भी विवाद की स्थिति में हैदराबाद, तेलंगाना, भारत के न्यायालयों का अधिकार क्षेत्र लागू होगा।'
                : 'These Terms & Conditions shall be governed by the laws of India. Any disputes arising shall fall under the jurisdiction of the courts of Hyderabad, Telangana, India.'}
            </p>

          </div>

          {/* Contact */}
          <div className="bg-gradient-to-r from-[#1A1A1A] to-[#111111] border border-[#C9A84C]/20 rounded-3xl p-8 text-center">

            <Phone className="text-[#C9A84C] mx-auto mb-5" size={34} />

            <h2 className="text-3xl font-serif text-[#F5F2EB] mb-4">
              {isHindi
                ? 'संपर्क करें'
                : 'Contact Us'}
            </h2>

            <p className="text-[#A3A3A3] leading-relaxed mb-6">
              {isHindi
                ? 'यदि आपको इन नियमों एवं शर्तों के संबंध में कोई प्रश्न हो, तो कृपया हमसे संपर्क करें।'
                : 'If you have any questions regarding these Terms & Conditions, please contact us.'}
            </p>

            <div className="space-y-3 text-[#D4D4D4]">

              <p>Email: astrologytimes5@gmail.com</p>

              <p>Phone: +91 XXXXX XXXXX</p>

            </div>

          </div>

          {/* Footer */}
          <div className="text-center pt-8">

            <p className="text-[#666666] text-sm leading-relaxed">
              {isHindi
                ? 'इस वेबसाइट एवं सेवाओं का उपयोग करके, आप इन नियमों एवं शर्तों से सहमत होते हैं।'
                : 'By using this website and our services, you acknowledge and agree to these Terms & Conditions.'}
            </p>

          </div>

        </div>

      </div>

    </div>
  );
};

export default TermsPage;